/**
 * useInfiniteScroll — auto-load more items when the user scrolls near the end.
 *
 * Attach the returned `sentinelRef` to a <div /> rendered as the very last
 * element of your paginated list. When that sentinel becomes visible in the
 * viewport (80% by default), `onLoadMore()` fires once.
 *
 * Behaviour:
 *   - No-ops when `hasMore` is false
 *   - Throttled: the observer is disconnected during a load and re-attached
 *     after a short cooldown to avoid rapid double-loads on fast scrolls
 *   - Re-observes automatically when `hasMore` flips false→true (e.g. filter
 *     change revealed more items)
 *
 * Returns the ref to assign to your sentinel element.
 */
import { useEffect, useRef } from 'react';

export default function useInfiniteScroll({ hasMore, onLoadMore, rootMargin = '300px' }) {
  const sentinelRef = useRef(null);
  const cooldownRef = useRef(false);
  const onLoadMoreRef = useRef(onLoadMore);

  // Keep the latest callback without forcing the observer to re-attach
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    if (!hasMore) return;
    const node = sentinelRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || cooldownRef.current) return;
        cooldownRef.current = true;
        onLoadMoreRef.current?.();
        // Cooldown so we don't fire during layout/paint
        setTimeout(() => { cooldownRef.current = false; }, 400);
      },
      { root: null, rootMargin, threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, rootMargin]);

  return sentinelRef;
}
